// import ADsense from 'components/landing/components/adsense';
import { useEffect, useState } from 'react';

import BlogSection from 'components/landing/blog-section/blog-section';
import Breadcrumb from 'components/seo/breadcrumb/breadcrumb';
import { cleanDictForJson } from 'utils/secure-json';
import CtaStickyBanner from 'components/home/cta-sticky-banner/cta-sticky-banner';
import CustomHead from 'core/custom-head';
import Deductibles from 'components/landing/deductibles/deductibles';
import Discount from 'components/landing/discount/discount';
import dynamic from 'next/dynamic';
import Footer from 'components/footer/footer';
import getCarName from 'utils/get-car-name';
import getConstants from 'utils/query-data-parser/landing/get-constant';
import Header from 'components/home/header';
import { Header as HeaderSection } from 'components/header/header';
import InformationLinks from 'components/landing/information-links/information-links';
import InsuranceCompanySelectorTable from 'components/landing/tables/insurance-selector/insurance-company-selector';
import InsuranceProfileSection from 'components/landing/company-information/company-information-section';
import Journey from 'components/landing/journey/journey';
import LandingContextProvider from 'components/landing/context/landing-context-provider';
import LocalitiesListSection from 'components/landing/localities-list/localities-list-section';
import ModifyLocationForm from 'components/landing/modify-location/modify-location-form';
import { PageInfoContextProvider } from 'context/PageInfoContext';
import PremiumPrice from 'components/landing/premium-price/premium-price';
import PropTypes from 'prop-types';
import ScriptContainer from 'components/container/script-container';
import Scroller from 'components/home/scroller';
import SeoTracking from 'components/seo/tracking/seo-tracking';
import Testimonial from 'components/home/testimonial/testimonial';
import TopRatedInsurance from 'components/landing/top-rated-insurance/top-rated-insurance';
import { useScroll } from 'hooks';
import withHydrationOnDemand from 'react-hydration-on-demand';

const Top5 = dynamic(() => import('components/landing/top-5/top-5'));
const HorizontalTable = dynamic(() => import('components/landing/tables/horizontal-table/horizontal-table'));
const PilarInsuranceCompanySection = dynamic(() => import('components/landing/pillar-insurance-company/pilar-insurance-company-section'));
const JourneyH = withHydrationOnDemand({ on: ['visible'] })(Journey);
const DiscountH = withHydrationOnDemand({ on: ['visible'] })(Discount);
const InsuranceCompanySelectorTableH = withHydrationOnDemand({ on: ['visible'] })(InsuranceCompanySelectorTable);
const DeductiblesH = withHydrationOnDemand({ on: ['visible'] })(Deductibles);
const InsuranceProfileSectionH = withHydrationOnDemand({ on: ['visible'] })(InsuranceProfileSection);
const PremiumPriceH = withHydrationOnDemand({ on: ['visible'] })(PremiumPrice);
const TopRatedInsuranceH = withHydrationOnDemand({ on: ['visible'] })(TopRatedInsurance);
const InformationLinksH = withHydrationOnDemand({ on: ['visible'] })(InformationLinks);
const ModifyLocationFormH = withHydrationOnDemand({ on: ['visible'] })(ModifyLocationForm);
const BlogSectionH = withHydrationOnDemand({ on: ['visible'] })(BlogSection);
const TestimonialH = withHydrationOnDemand({ on: ['visible'] })(Testimonial);
const LocalitiesListSectionH = withHydrationOnDemand({ on: ['visible'] })(LocalitiesListSection);
const FooterH = withHydrationOnDemand({ on: ['visible'] })(Footer);
const BreadcrumbH = withHydrationOnDemand({ on: ['visible'] })(Breadcrumb);

const Landing = ({ culture, payload, carName }) => {
    const [loadTotalPage, setLoadTotalPage] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setLoadTotalPage(true);
        }, 3000);
    }, []);

    useScroll((scroll) => {
        if (scroll > 0 && !loadTotalPage) {
            setLoadTotalPage(true);
        }
    }, [loadTotalPage]);

    return (
        <ScriptContainer breadcrumbList={payload.page_context.page_breadcrumb_list}>
            <SeoTracking templateName="contentap" pageCode={payload.page_context.code} />
            <CustomHead pageInfo={payload} />
            <PageInfoContextProvider data={payload}>
                <LandingContextProvider>
                    <CtaStickyBanner hideMobile />
                    <Scroller />
                    <Header />
                    <HeaderSection bannerType="old-landing" />
                    <BreadcrumbH />
                    {loadTotalPage && (
                        <>
                            <PilarInsuranceCompanySection
                                familyCodeList={payload.page_context.page_dinamic_data.family_codes}
                                pageCode={payload.page_context.code}
                                discount={payload.page_context.page_config.env.discount}
                            />
                            <Top5
                                pageCode={payload.page_context.code}
                                discount={payload.page_context.page_config.env.discount}
                            />
                        </>
                    )}
                    <JourneyH />
                    {loadTotalPage && (
                        <HorizontalTable
                            pageCode={payload.page_context.code}
                            culture={culture}
                            discount={payload.page_context.page_config.env.discount}
                            carName={carName}
                        />
                    )}
                    <DiscountH data={payload.page_context.page_dinamic_data.discount_list} />
                    <InsuranceCompanySelectorTableH
                        data={payload.page_context.page_dinamic_data.related_insurers}
                        culture={culture}
                    />
                    <DeductiblesH />
                    <InsuranceProfileSectionH />
                    <PremiumPriceH data={payload.page_context.page_dinamic_data.car_quotation_range} />
                    <TopRatedInsuranceH
                        pageCode={payload.page_context.code}
                        template={payload.template_type}
                        companyRating={payload.page_context.page_dinamic_data.insurance_company_rating}
                    />
                    <InformationLinksH data={payload.page_context.page_dinamic_data.link_list} />
                    <ModifyLocationFormH />
                    <BlogSectionH />
                    <TestimonialH />
                    <LocalitiesListSectionH />
                    <FooterH />
                </LandingContextProvider>
            </PageInfoContextProvider>
        </ScriptContainer>
    );
};

export async function getServerSideProps ({ locale, req }) {
    const data = req.apiData.data;

    const payload = {
        ...data,
        ...getConstants(data.page_context)
    };

    const carName = getCarName(
        payload.page_context.deep_level,
        payload,
        payload.template_type
    );

    return {
        props: {
            messages: (await import(`../messages/insurance-company/${locale}.json`))
                .default,
            culture: locale,
            carName,
            payload: cleanDictForJson(payload)
        }
    };
};

Landing.propTypes = {
    culture: PropTypes.string,
    payload: PropTypes.object,
    carName: PropTypes.string
};

export default Landing;
